import React from 'react';
import { Box, Typography } from '@mui/material';
import useMenteePeers from '../../../../../queries/useMenteePeers';
import { getUserDetails } from '../../../../../utils/orgName';
import { UserProgramInfo } from '../utils/programTypes';
import { MemberRole, isMemberRole, useMemberWord } from '../../../../../utils/memberTypeLabels';

interface Props {
  program: UserProgramInfo;
}

const UserProgramCard: React.FC<Props> = ({ program }) => {
  const w = useMemberWord();
  // program.roles are the membership's role codes ("Mentee", "Mentor"), shown as the org's own word.
  const primaryRole = program.roles
    .map((role) => (isMemberRole(role) ? w(role.trim().toLowerCase() as MemberRole) : role))
    .join(' / ');
  const hasMentorRole = program.roles.some((role) => role.toLowerCase().includes('mentor'));
  const { orgId, location } = getUserDetails();
  const { data: menteePeersData } = useMenteePeers({ grpId: program.id, orgId, location });

  const peerProgress = menteePeersData?.data?.data?.[0]?.avgGoalProgress;
  const progress =
    typeof peerProgress === 'number'
      ? peerProgress
      : typeof program.progress === 'number'
      ? program.progress
      : undefined;

  const objectivesText = program.objectivesPending
    ? `${program.objectivesPending} objectives yet to be completed`
    : '~ Time info unavailable';

  return (
    <Box
      sx={{
        width: '100%',
        p: 2.5,
        background: '#151515',
        borderRadius: '5px',
        outline: '2px solid #FDFDFD',
        outlineOffset: '-2px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5
      }}
      onClick={program.onClick}
      role={program.onClick ? 'button' : undefined}
      tabIndex={program.onClick ? 0 : undefined}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box
            sx={{
              px: 1,
              py: 0.5,
              background: '#F15A26',
              borderRadius: '20px',
              display: 'inline-flex',
              alignItems: 'center'
            }}
          >
            <Typography
              sx={{ color: '#FDFDFD', fontSize: 12, fontWeight: 400, lineHeight: '14px', fontFamily: 'Inter' }}
            >
              {program.type || 'Program'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ color: '#868686', fontSize: 12, fontWeight: 400, fontFamily: 'Inter' }}>Role:</Typography>
            <Box sx={{ overflow: 'hidden', borderRadius: '6px', display: 'flex' }}>
              <Box sx={{ px: 1, py: 0.7, background: '#444444', display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ color: '#FDFDFD', fontSize: 10, fontWeight: 700, fontFamily: 'Inter' }}>
                  {primaryRole}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Typography
          sx={{
            color: '#FDFDFD',
            fontSize: 22,
            fontWeight: 600,
            textTransform: 'capitalize',
            fontFamily: 'Inter',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {program.name}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Typography sx={{ color: '#B6B6B6', fontSize: 14, fontWeight: 400, fontFamily: 'Inter' }}>
          Program Progress: {progress !== undefined ? `${progress}%` : 'N/A'}
        </Typography>
        <Box sx={{ height: 6, background: '#2A2A2A', borderRadius: 3, overflow: 'hidden' }}>
          <Box
            sx={{
              height: '100%',
              width: `${Math.min(Math.max(progress || 0, 0), 100)}%`,
              background: '#F0B80D'
            }}
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
        {hasMentorRole ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
            <Typography sx={{ color: '#868686', fontSize: 12, fontWeight: 600, fontFamily: 'Inter' }}>
              {program.menteesEngaged ?? 0} {w('mentee', { plural: true })} Engaged
            </Typography>
            <Typography sx={{ color: '#868686', fontSize: 12, fontWeight: 600, fontFamily: 'Inter' }}>
              {program.messagesSent ?? 0} Messages Sent
            </Typography>
            <Typography sx={{ color: '#868686', fontSize: 12, fontWeight: 600, fontFamily: 'Inter' }}>
              {program.meetingsScheduled ?? 0} Meetings Scheduled
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
            <Box sx={{ width: 16, height: 16, position: 'relative' }}>
              <Box sx={{ position: 'absolute', inset: 0, border: '1.5px solid #868686' }} />
            </Box>
            <Typography sx={{ color: '#868686', fontSize: 12, fontWeight: 600, fontFamily: 'Inter' }}>
              {program.etaText || objectivesText}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            px: 1.25,
            py: 0.5,
            background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography sx={{ color: '#FDFDFD', fontSize: 12, fontWeight: 600, fontFamily: 'Inter' }}>
            Open
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(UserProgramCard);
